import { useEffect, useState } from "react";
import { hueFor } from "../Icons";
import type { PublicSlide } from "../../lib/types";

/** What a phone shows for each slide type. */

interface Props {
  slide: PublicSlide;
  answersUsed: number;
  answersAllowed: number;
  onAnswer: (value: unknown) => void;
  disabled: boolean;
}

export function AnswerInput({ slide, answersUsed, answersAllowed, onAnswer, disabled }: Props) {
  const config = slide.config as Record<string, any>;

  switch (slide.type) {
    case "quiz":
      return <Options options={config.options ?? []} onPick={onAnswer} disabled={disabled} />;
    case "multiple":
      return config.multi ? (
        <MultiOptions
          options={config.options ?? []}
          limit={config.max_choices ?? null}
          onSend={onAnswer}
          disabled={disabled}
        />
      ) : (
        <Options options={config.options ?? []} onPick={onAnswer} disabled={disabled} />
      );
    case "scale":
      return <Scale config={config} onPick={onAnswer} disabled={disabled} />;
    case "wordcloud":
      return (
        <WordEntry
          left={answersAllowed - answersUsed}
          onSend={onAnswer}
          disabled={disabled}
        />
      );
    case "open":
      return <OpenEntry max={config.max_length ?? 280} onSend={onAnswer} disabled={disabled} />;
    case "guess":
      return <GuessEntry config={config} onSend={onAnswer} disabled={disabled} />;
    case "ranking":
      return <RankPicker options={config.options ?? []} onSend={onAnswer} disabled={disabled} />;
    case "grid":
      return <GridPicker config={config} onSend={onAnswer} disabled={disabled} />;
    default:
      return null;
  }
}

function Options({
  options,
  onPick,
  disabled,
}: {
  options: string[];
  onPick: (v: number) => void;
  disabled: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {options.map((option, i) => (
        <button
          key={i}
          className="btn btn-outline"
          disabled={disabled}
          onClick={() => onPick(i)}
          style={{ height: 60, fontSize: 17, justifyContent: "flex-start", gap: 14 }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "var(--radius-pill)",
              background: hueFor(i),
              flex: "none",
            }}
          />
          {option}
        </button>
      ))}
    </div>
  );
}

function MultiOptions({
  options,
  limit,
  onSend,
  disabled,
}: {
  options: string[];
  limit: number | null;
  onSend: (v: number[]) => void;
  disabled: boolean;
}) {
  const [picked, setPicked] = useState<number[]>([]);
  const [sending, setSending] = useState(false);
  const atLimit = limit != null && picked.length >= limit;

  useEffect(() => {
    if (!sending) return;
    const timer = window.setTimeout(() => setSending(false), 4000);
    return () => window.clearTimeout(timer);
  }, [sending]);

  function toggle(index: number) {
    if (disabled || sending) return;
    setPicked((current) =>
      current.includes(index)
        ? current.filter((i) => i !== index)
        : atLimit
          ? current
          : [...current, index],
    );
  }

  return (
    <>
      <p className="tiny" style={{ marginBottom: 12 }}>
        {limit ? `Choose up to ${limit}.` : "Choose as many as you like."}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {options.map((option, i) => {
          const on = picked.includes(i);
          // A full selection greys the rest, so the cap is visible rather
          // than only discovered by tapping.
          const blocked = !on && atLimit;
          return (
            <button
              key={i}
              disabled={disabled || sending || blocked}
              onClick={() => toggle(i)}
              aria-pressed={on}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                height: 60,
                padding: "0 16px",
                borderRadius: "var(--radius-control)",
                border: `1px solid ${on ? "var(--primary)" : "var(--outline)"}`,
                background: on ? "var(--primary-container)" : "var(--surface)",
                opacity: blocked ? 0.45 : 1,
                cursor: disabled || blocked ? "not-allowed" : "pointer",
                fontSize: 17,
                textAlign: "left",
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  flex: "none",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 7,
                  border: on ? "none" : "1.5px solid var(--outline)",
                  background: on ? "var(--primary)" : "transparent",
                  color: "#fff",
                  fontSize: 14,
                  lineHeight: 1,
                }}
              >
                {on ? "✓" : ""}
              </span>
              <span style={{ flex: 1 }}>{option}</span>
            </button>
          );
        })}
      </div>
      <button
        className="btn"
        style={{ width: "100%", marginTop: 16 }}
        disabled={disabled || sending || picked.length === 0}
        onClick={() => {
          setSending(true);
          onSend([...picked].sort((a, b) => a - b));
        }}
      >
        {sending
          ? "Sending…"
          : picked.length === 0
            ? "Pick at least one"
            : `Submit ${picked.length} answer${picked.length > 1 ? "s" : ""}`}
      </button>
    </>
  );
}

function Scale({
  config,
  onPick,
  disabled,
}: {
  config: Record<string, any>;
  onPick: (v: number) => void;
  disabled: boolean;
}) {
  return (
    <>
      <div style={{ display: "flex", gap: 10 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            className="btn btn-outline"
            disabled={disabled}
            onClick={() => onPick(n)}
            style={{ flex: 1, height: 64, fontSize: 20, fontWeight: 700, padding: 0 }}
          >
            {n}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
        <span className="tiny">{config.min_label}</span>
        <span className="tiny">{config.max_label}</span>
      </div>
    </>
  );
}

function WordEntry({
  left,
  onSend,
  disabled,
}: {
  left: number;
  onSend: (v: string) => void;
  disabled: boolean;
}) {
  const [value, setValue] = useState("");
  function send() {
    const word = value.trim();
    if (!word) return;
    onSend(word);
    setValue("");
  }
  return (
    <>
      <div style={{ display: "flex", gap: 10 }}>
        <input
          className="input"
          style={{ height: 54, fontSize: 17 }}
          placeholder="One word"
          maxLength={40}
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
        />
        <button className="btn" style={{ height: 54 }} disabled={disabled || !value.trim()} onClick={send}>
          Send
        </button>
      </div>
      <p className="tiny" style={{ marginTop: 10, textAlign: "center" }}>
        {left} of your words left
      </p>
    </>
  );
}

function OpenEntry({
  max,
  onSend,
  disabled,
}: {
  max: number;
  onSend: (v: string) => void;
  disabled: boolean;
}) {
  const [value, setValue] = useState("");
  return (
    <>
      <textarea
        className="input"
        style={{ height: 130, padding: 14, resize: "none", lineHeight: 1.5 }}
        placeholder="Type your answer"
        maxLength={max}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
        <span className="tiny">
          {value.length} / {max}
        </span>
        <div style={{ flex: 1 }} />
        <button className="btn" disabled={disabled || !value.trim()} onClick={() => onSend(value.trim())}>
          Send
        </button>
      </div>
    </>
  );
}

function GuessEntry({
  config,
  onSend,
  disabled,
}: {
  config: Record<string, any>;
  onSend: (v: number) => void;
  disabled: boolean;
}) {
  const [value, setValue] = useState("");
  const number = Number(value);
  const valid = value !== "" && !Number.isNaN(number) && number >= config.lo && number <= config.hi;
  return (
    <>
      <input
        className="input"
        type="number"
        inputMode="numeric"
        style={{ height: 64, fontSize: 26, fontWeight: 700, textAlign: "center" }}
        placeholder="?"
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
      />
      <p className="tiny" style={{ textAlign: "center", margin: "10px 0 14px" }}>
        somewhere between {config.lo} and {config.hi}
      </p>
      <button
        className="btn"
        style={{ width: "100%" }}
        disabled={disabled || !valid}
        onClick={() => onSend(number)}
      >
        Submit guess
      </button>
    </>
  );
}

function RankPicker({
  options,
  onSend,
  disabled,
}: {
  options: string[];
  onSend: (v: number[]) => void;
  disabled: boolean;
}) {
  const [order, setOrder] = useState<number[]>([]);
  const [sending, setSending] = useState(false);
  const remaining = options.length - order.length;

  // If the send is refused the parent shows why, so let them try again
  // rather than leaving the button dead.
  useEffect(() => {
    if (!sending) return;
    const timer = window.setTimeout(() => setSending(false), 4000);
    return () => window.clearTimeout(timer);
  }, [sending]);

  function toggle(i: number) {
    if (disabled || sending) return;
    const next = order.includes(i) ? order.filter((x) => x !== i) : [...order, i];
    setOrder(next);
    // Send on the final tap. A separate submit step meant a half-finished
    // ranking looked answered on the phone while nothing had left it, and
    // the presenter waited on a question everyone thought they'd answered.
    if (next.length === options.length) {
      setSending(true);
      onSend(next);
    }
  }

  return (
    <>
      <p className="tiny" style={{ marginBottom: 12 }}>
        {remaining === options.length
          ? `Tap all ${options.length} in order — most important first.`
          : remaining > 0
            ? `Tap ${remaining} more to send your ranking.`
            : "Sending your ranking…"}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {options.map((option, i) => {
          const rank = order.indexOf(i);
          const picked = rank >= 0;
          return (
            <button
              key={i}
              disabled={disabled || sending}
              onClick={() => toggle(i)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                height: 56,
                padding: "0 16px",
                borderRadius: "var(--radius-control)",
                border: `1px solid ${picked ? "var(--primary)" : "var(--outline)"}`,
                background: picked ? "var(--primary-container)" : "var(--surface)",
                cursor: disabled ? "not-allowed" : "pointer",
                fontSize: 16,
                textAlign: "left",
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "var(--radius-pill)",
                  background: picked ? "var(--primary)" : "transparent",
                  border: picked ? "none" : "1.5px dashed var(--outline)",
                  color: picked ? "#fff" : "var(--text-tertiary)",
                  fontSize: 13,
                  fontWeight: 700,
                  flex: "none",
                }}
              >
                {picked ? rank + 1 : ""}
              </span>
              <span style={{ flex: 1 }}>{option}</span>
              {!picked && (
                <span className="tiny" style={{ flex: "none" }}>
                  tap to rank
                </span>
              )}
            </button>
          );
        })}
      </div>
      {remaining > 0 && (
        <div
          style={{
            marginTop: 14,
            height: 4,
            borderRadius: 999,
            background: "var(--surface-variant)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${(order.length / options.length) * 100}%`,
              background: "var(--primary)",
              borderRadius: 999,
              transition: "width 200ms var(--ease-default)",
            }}
          />
        </div>
      )}
      {remaining === 0 && (
        <button
          className="btn"
          style={{ width: "100%", marginTop: 16 }}
          disabled={disabled || sending}
          onClick={() => {
            setSending(true);
            onSend(order);
          }}
        >
          {sending ? "Sending…" : "Send ranking again"}
        </button>
      )}
    </>
  );
}

function GridPicker({
  config,
  onSend,
  disabled,
}: {
  config: Record<string, any>;
  onSend: (v: { x: number; y: number }) => void;
  disabled: boolean;
}) {
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);

  function place(event: React.MouseEvent<HTMLDivElement>) {
    if (disabled) return;
    const box = event.currentTarget.getBoundingClientRect();
    setPoint({
      x: Math.min(0.97, Math.max(0.03, (event.clientX - box.left) / box.width)),
      y: Math.min(0.97, Math.max(0.03, (event.clientY - box.top) / box.height)),
    });
  }

  return (
    <>
      <div className="tiny" style={{ textAlign: "center", marginBottom: 4 }}>
        {config.y_hi}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="tiny" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
          {config.x_lo}
        </span>
        <div
          onClick={place}
          style={{
            position: "relative",
            flex: 1,
            aspectRatio: "1",
            background: "var(--surface)",
            border: "1px solid var(--outline)",
            borderRadius: 16,
            cursor: disabled ? "not-allowed" : "crosshair",
          }}
        >
          <div style={{ position: "absolute", inset: "50% 0 auto", borderTop: "1px dashed var(--outline)" }} />
          <div style={{ position: "absolute", inset: "0 auto 0 50%", borderLeft: "1px dashed var(--outline)" }} />
          {point && (
            <span
              style={{
                position: "absolute",
                left: `${point.x * 100}%`,
                top: `${point.y * 100}%`,
                width: 20,
                height: 20,
                marginLeft: -10,
                marginTop: -10,
                borderRadius: "var(--radius-pill)",
                background: "var(--primary)",
                border: "2px solid #fff",
                animation: "dotIn 200ms var(--ease-default)",
              }}
            />
          )}
        </div>
        <span className="tiny" style={{ writingMode: "vertical-rl" }}>
          {config.x_hi}
        </span>
      </div>
      <div className="tiny" style={{ textAlign: "center", marginTop: 4 }}>
        {config.y_lo}
      </div>
      <button
        className="btn"
        style={{ width: "100%", marginTop: 16 }}
        disabled={disabled || !point}
        onClick={() => point && onSend(point)}
      >
        {point ? "Place my dot" : "Tap the grid"}
      </button>
    </>
  );
}
